import React from 'react';
import { reportClientError } from '../../api/errorReporter';
import './ErrorBoundary.css';

interface Props {
  children: React.ReactNode;
  /**
   * What failed, for the message and for the log. "the workshop console" reads
   * better than a component name and tells the user which part is broken.
   */
  area?: string;
  /** Changing this resets the boundary -- pass the route so navigating away recovers. */
  resetKey?: string;
  /**
   * What to draw instead of the broken page, e.g. the 500 page (features/errors). It gets
   * the error and a short code ("ERR-7F3A") that is also in the report, so whoever
   * reports it can quote it and an admin can find it in Admin Center > Errors.
   * Without one, the boundary's own plain message shows.
   */
  fallback?: (failure: { error: Error; code: string }) => React.ReactNode;
}

interface State {
  error: Error | null;
  code: string;
}

/** "ERR-7F3A": four hex digits, enough to tell one report from the next. */
function errorCode(): string {
  return `ERR-${Math.floor(Math.random() * 0x10000).toString(16).toUpperCase().padStart(4, '0')}`;
}

/**
 * Stops one broken component taking the whole app down.
 *
 * React unmounts the entire tree on an unhandled render error. With no boundary
 * anywhere, a single bad property access rendered a blank white page with no
 * navigation, no message, and nothing to click -- twice during recent work, both
 * times from a one-line mistake. The user's only recourse was to guess that a
 * reload might help.
 *
 * A class component because this is the one thing hooks cannot do.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, code: '' };

  static getDerivedStateFromError(error: Error): State {
    return { error, code: errorCode() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.area || 'app'}] render failed`, error, info.componentStack);
    // And to Admin Center > Errors, which is where someone will actually see it.
    reportClientError(`render:${this.props.area || 'app'}`, error, {
      componentStack: info.componentStack || undefined,
      code: this.state.code || undefined,
    });
  }

  componentDidUpdate(prev: Props) {
    // Navigating away from a broken page must not stay broken.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, code: '' });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback({ error: this.state.error, code: this.state.code });

    return (
      <div role="alert" className="error-boundary">
        <h2 className="error-boundary__title">Something on this page broke</h2>
        <p className="error-boundary__body">
          The rest of Grow is still working. Reloading usually clears it; if it keeps
          happening, this is a bug worth reporting.
        </p>
        <div className="error-boundary__actions">
          <button
            type="button"
            className="error-boundary__btn error-boundary__btn--primary"
            onClick={() => window.location.reload()}
          >
            Reload the page
          </button>
          <a href="/dashboard" className="error-boundary__btn">Go to the dashboard</a>
        </div>
        {/* The message only, never the stack: it is meaningless to a user and can
            carry internals. The full trace is in the console for whoever needs it. */}
        <p className="error-boundary__detail">{this.state.error.message}</p>
      </div>
    );
  }
}

export default ErrorBoundary;
